import React, { useState } from 'react';
import { getAuthToken } from '../../../utils/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SEOModule = ({ user }) => {
  const [activeTemplate, setActiveTemplate] = useState('blog-article');
  const [formData, setFormData] = useState({
    keyword: '',
    secondaryKeywords: '',
    topic: '',
    wordCount: 1500,
    language: 'fr'
  });
  const [generating, setGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState('');

  const templates = [
    { id: 'blog-article', name: 'Article de blog SEO', icon: '📝', desc: 'Article optimisé avec structure H1-H3' },
    { id: 'meta-title', name: 'Meta Title', icon: '🏷️', desc: 'Titre SEO optimisé 60 caractères' },
    { id: 'meta-description', name: 'Meta Description', icon: '📋', desc: 'Description SEO 155 caractères' },
    { id: 'faq-seo', name: 'FAQ SEO', icon: '❓', desc: 'Questions/réponses optimisées' },
    { id: 'semantic-cocoon', name: 'Cocon sémantique', icon: '🕸️', desc: 'Structure de liens internes' },
    { id: 'content-optimization', name: 'Optimisation contenu', icon: '🔧', desc: 'Améliorer un contenu existant' }
  ];

  const handleGenerate = async () => {
    if (!formData.keyword) return;
    setGenerating(true);
    
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_URL}/api/generate-content`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          content_type: 'blog',
          topic: formData.topic || formData.keyword,
          tone: 'professional',
          word_count: formData.wordCount,
          additional_instructions: `
            Type: ${activeTemplate}
            Mot-clé principal: ${formData.keyword}
            Mots-clés secondaires: ${formData.secondaryKeywords}
            
            Instructions SEO:
            - Optimiser pour le mot-clé principal
            - Inclure les mots-clés secondaires naturellement
            - Structure avec H1, H2, H3
            - Densité de mots-clés 1-2%
            - Introduction avec le mot-clé dans les 100 premiers mots
            - Meta description si demandé
            - FAQ schema-ready si template FAQ
          `
        })
      });

      if (response.ok) {
        const data = await response.json();
        setGeneratedContent(data.content);
      }
    } catch (error) {
      console.error('SEO generation error:', error);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="grid grid-cols-12 gap-6">
      {/* Templates */}
      <div className="col-span-3 space-y-3">
        <h3 className="text-white font-semibold mb-4">Templates SEO</h3>
        {templates.map(template => (
          <button
            key={template.id}
            onClick={() => setActiveTemplate(template.id)}
            className={`w-full text-left p-4 rounded-xl transition-all ${
              activeTemplate === template.id
                ? 'bg-blue-500/20 border border-blue-500/30'
                : 'bg-[#12121a] border border-gray-800 hover:border-gray-700'
            }`}
          >
            <div className="flex items-center space-x-3">
              <span className="text-2xl">{template.icon}</span>
              <div>
                <p className={`font-medium ${activeTemplate === template.id ? 'text-blue-400' : 'text-white'}`}>
                  {template.name}
                </p>
                <p className="text-xs text-gray-500">{template.desc}</p>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Configuration */}
      <div className="col-span-4 bg-[#12121a] rounded-2xl border border-gray-800 p-6">
        <h3 className="text-white font-semibold mb-6">Configuration SEO</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-gray-400 mb-2">Mot-clé principal *</label>
            <input
              type="text"
              value={formData.keyword}
              onChange={(e) => setFormData({...formData, keyword: e.target.value})}
              placeholder="Ex: marketing digital 2025"
              className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-blue-500 transition-all"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2">Mots-clés secondaires</label>
            <input
              type="text"
              value={formData.secondaryKeywords}
              onChange={(e) => setFormData({...formData, secondaryKeywords: e.target.value})}
              placeholder="Ex: stratégie digitale, SEO, content marketing"
              className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-blue-500 transition-all"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2">Sujet / Angle</label>
            <textarea
              value={formData.topic}
              onChange={(e) => setFormData({...formData, topic: e.target.value})}
              placeholder="Décrivez l'angle ou le sujet spécifique..."
              rows={3}
              className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-blue-500 transition-all resize-none"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2">Nombre de mots</label>
            <select
              value={formData.wordCount}
              onChange={(e) => setFormData({...formData, wordCount: parseInt(e.target.value)})}
              className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-blue-500 transition-all"
            >
              <option value={500}>500 mots (court)</option>
              <option value={1000}>1000 mots</option>
              <option value={1500}>1500 mots (recommandé)</option>
              <option value={2000}>2000 mots</option>
              <option value={3000}>3000 mots (long)</option>
            </select>
          </div>

          <button
            onClick={handleGenerate}
            disabled={generating || !formData.keyword}
            className="w-full py-4 bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 disabled:from-gray-600 disabled:to-gray-700 rounded-xl font-semibold text-white transition-all"
          >
            {generating ? 'Génération SEO...' : 'Générer le contenu SEO'}
          </button>
        </div>
      </div>

      {/* Output */}
      <div className="col-span-5 bg-[#12121a] rounded-2xl border border-gray-800 overflow-hidden flex flex-col">
        <div className="p-4 border-b border-gray-800">
          <h3 className="text-white font-semibold">Contenu SEO généré</h3>
        </div>
        <div className="flex-1 p-4 overflow-y-auto">
          {generating ? (
            <div className="h-full flex items-center justify-center">
              <div className="text-center text-gray-500">
                <div className="w-12 h-12 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
                <p>Optimisation SEO en cours...</p>
              </div>
            </div>
          ) : generatedContent ? (
            <div className="prose prose-invert max-w-none">
              <div className="whitespace-pre-wrap text-gray-300">{generatedContent}</div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center text-gray-500">
              <div className="text-center">
                <span className="text-4xl mb-4 block">🔍</span>
                <p>Configurez vos paramètres SEO<br/>pour générer du contenu optimisé</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SEOModule;
